import React, { useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { IntelChip, IntelPopupCard, INTEL_COLORS, humanize, type IntelAction, type IntelRow } from './IntelPopup';
import {
  REFERENCE_LAYER_TYPE_LABELS,
  type ReferenceLayerDto,
  type ReferenceLayerStyle,
} from '../../services/referenceLayerApi';

type Props = {
  layer: ReferenceLayerDto;
  style: ReferenceLayerStyle;
  properties: Record<string, unknown>;
  /** Where the user clicked (polygons have no single position). */
  coords?: { lat: number; lon: number } | null;
  onClose?: () => void;
  actions?: IntelAction[];
};

const MAX_INLINE = 8;
const HIDDEN = new Set(['id', 'objectid', 'fid', 'shape_area', 'shape_length', 'shape_leng', 'styleurl']);

function fmt(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'number') return Number.isInteger(value) ? value.toLocaleString() : value.toLocaleString(undefined, { maximumFractionDigits: 3 });
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  const s = String(value);
  return s.length > 160 ? `${s.slice(0, 157)}…` : s;
}

/** Popup body for a clicked reference-layer feature — same intel card as cameras/devices. */
const ReferenceFeaturePopupContent: React.FC<Props> = ({ layer, style, properties, coords, onClose, actions = [] }) => {
  const [showAll, setShowAll] = useState(false);
  const labelField = layer.label_field;
  const title = labelField && properties[labelField] != null ? fmt(properties[labelField]) : layer.name;

  const entries = useMemo(() => {
    const colorField = style.color_by?.field;
    const rank = (k: string) => (k === colorField ? 0 : 1);
    return Object.entries(properties)
      .filter(([k, v]) => k !== labelField && v != null && v !== '' && !HIDDEN.has(k.toLowerCase()))
      .sort((a, b) => rank(a[0]) - rank(b[0]) || a[0].localeCompare(b[0]));
  }, [properties, labelField, style.color_by]);

  const inline = showAll ? entries : entries.slice(0, MAX_INLINE);
  const hiddenCount = entries.length - inline.length;

  const rows: IntelRow[] = inline.map(([k, v]) => ({ label: humanize(k), value: fmt(v) }));

  const allActions: IntelAction[] = [
    ...(hiddenCount > 0 || showAll
      ? [{ label: showAll ? 'Less' : `+${hiddenCount} more`, onClick: () => setShowAll((s) => !s), color: INTEL_COLORS.textMuted }]
      : []),
    ...actions,
  ];

  return (
    <IntelPopupCard
      title={title}
      subtitle={title === layer.name ? undefined : layer.name}
      chip={<IntelChip color={style.color} filled>{REFERENCE_LAYER_TYPE_LABELS[layer.layer_type] || humanize(layer.layer_type)}</IntelChip>}
      accent={style.color}
      rows={rows}
      coords={coords ?? null}
      actions={allActions}
      onClose={onClose}
    >
      {entries.length === 0 && <Box sx={{ fontSize: '0.72rem', color: INTEL_COLORS.textDim }}>No attributes.</Box>}
    </IntelPopupCard>
  );
};

export default ReferenceFeaturePopupContent;
